import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Stack, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, List, ListItem, ListItemText, ListItemIcon, Alert,
  TextField, Autocomplete,
  Stepper, Step, StepLabel,
  IconButton, Tooltip, Tabs, Tab,
  Switch, FormControlLabel, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import type { IScanTemplate, IScoringRule, ICategory, IScanTemplateCategory, ITemplateScoringRule } from '../../../shared/types.js';

const WIZARD_STEPS = ['Grunddaten', 'Kategorien', 'Scoring-Regeln'];

/* ── Drag & Drop Category List Component ───────────────────── */
interface CategoryListItem {
  category_id: string;
}

interface DragCategoryListProps {
  items: CategoryListItem[];
  categories: ICategory[];
  onChange: (items: CategoryListItem[]) => void;
  maxItems?: number;
}

function DragCategoryList({ items, categories, onChange, maxItems = 3 }: DragCategoryListProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const catName = (id: string) => categories.find((c) => c.id === id)?.display_name ?? id;

  const availableCategories = categories.filter(
    (cat) => !items.some((item) => item.category_id === cat.id),
  );

  const handleAdd = (categoryId: string) => {
    onChange([...items, { category_id: categoryId }]);
    setAddOpen(false);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleDragStart = (index: number) => {
    setDragIdx(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDropIdx(index);
  };

  const handleDragLeave = () => {
    setDropIdx(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIndex) {
      setDragIdx(null);
      setDropIdx(null);
      return;
    }
    const newItems = [...items];
    const [moved] = newItems.splice(dragIdx, 1);
    newItems.splice(targetIndex, 0, moved);
    onChange(newItems);
    setDragIdx(null);
    setDropIdx(null);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDropIdx(null);
  };

  return (
    <Box>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Noch keine Kategorien zugewiesen.
        </Typography>
      )}

      <Stack spacing={0}>
        {items.map((item, index) => (
          <Box key={item.category_id}>
            {/* Drop indicator line above */}
            {dropIdx === index && dragIdx !== null && dragIdx !== index && (
              <Box sx={{ height: 3, bgcolor: colors.lime, borderRadius: 1, mx: 1 }} />
            )}
            <Box
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                mb: 0.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: dragIdx === index ? colors.lime : colors.silver,
                bgcolor: dragIdx === index ? 'rgba(206, 220, 0, 0.08)' : 'white',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                transition: 'all 0.15s ease',
              }}
            >
              <DragIndicatorIcon sx={{ color: colors.silver, mr: 1.5, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                {catName(item.category_id)}
              </Typography>
              <Chip
                label={`Slot ${index + 1}`}
                size="small"
                sx={{ bgcolor: colors.lightGrey, fontWeight: 600, mr: 1 }}
              />
              <IconButton size="small" onClick={() => handleRemove(index)} sx={{ color: 'error.main' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}

        {/* Drop indicator at the end */}
        {dropIdx === items.length && dragIdx !== null && (
          <Box sx={{ height: 3, bgcolor: colors.lime, borderRadius: 1, mx: 1 }} />
        )}

        {/* Drop zone at the end for reordering */}
        {items.length > 0 && dragIdx !== null && (
          <Box
            onDragOver={(e) => handleDragOver(e, items.length)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, items.length)}
            sx={{ height: 24 }}
          />
        )}
      </Stack>

      {/* Add button */}
      {items.length < maxItems && (
        <Box sx={{ mt: 2 }}>
          {!addOpen ? (
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddOpen(true)}
              variant="outlined"
              size="small"
              sx={{
                borderColor: colors.lime,
                borderWidth: 1.5,
                borderStyle: 'dashed',
                color: colors.black,
                fontWeight: 600,
                letterSpacing: 0.3,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderStyle: 'solid',
                  borderColor: colors.lime,
                  bgcolor: 'rgba(206, 220, 0, 0.10)',
                  boxShadow: '0 2px 8px rgba(206, 220, 0, 0.25)',
                },
              }}
            >
              Kategorie hinzufügen
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                options={availableCategories}
                getOptionLabel={(option) => option.display_name}
                onChange={(_event, value) => {
                  if (value) handleAdd(value.id);
                }}
                size="small"
                sx={{ minWidth: 280 }}
                noOptionsText="Keine Kategorien verfügbar"
                openOnFocus
                renderInput={(params) => (
                  <TextField {...params} label="Kategorie auswählen" autoFocus />
                )}
              />
              <Button
                size="small"
                onClick={() => setAddOpen(false)}
                sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
              >
                Abbrechen
              </Button>
            </Box>
          )}
        </Box>
      )}

      {items.length >= maxItems && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Maximal {maxItems} Kategorien erreicht.
        </Typography>
      )}
    </Box>
  );
}

/* ── Main Page Component ───────────────────────────────────── */
export default function ConfigPlusTemplatesPage() {
  const { user } = useAuth();
  // Der "Entwickler only"-Switch darf ausschliesslich im Entwicklermodus sichtbar sein
  const devMode = user?.dev_mode === true;
  const [templates, setTemplates] = useState<IScanTemplate[]>([]);
  const [scoringRules, setScoringRules] = useState<IScoringRule[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ── Create Wizard State ─────────────────────────────────── */
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [creating, setCreating] = useState(false);

  // Step 1 — Basic Info
  const [formInternalName, setFormInternalName] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formDescription, setFormDescription] = useState('');

  // Step 2 — Categories (ordered list)
  const [wizardCategories, setWizardCategories] = useState<CategoryListItem[]>([]);

  // Step 3 — Scoring Rules
  const [wizardRuleIds, setWizardRuleIds] = useState<string[]>([]);

  /* ── Search & Filter ────────────────────────────────────── */
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  /* ── Delete Template Dialog ──────────────────────────────── */
  const [deleteTarget, setDeleteTarget] = useState<IScanTemplate | null>(null);

  /* ── Full Config Dialog (Categories + Scoring Rules) ─────── */
  const [configTarget, setConfigTarget] = useState<IScanTemplate | null>(null);
  const [configCategories, setConfigCategories] = useState<CategoryListItem[]>([]);
  const [configRuleIds, setConfigRuleIds] = useState<string[]>([]);
  const [configTab, setConfigTab] = useState(0);
  const [configRuleSearch, setConfigRuleSearch] = useState('');
  const [configDevOnly, setConfigDevOnly] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, rulesRes, catsRes] = await Promise.all([
        api.get<{ templates: IScanTemplate[] }>('/config/scan-templates'),
        api.get<IScoringRule[]>('/config-plus/scoring-rules'),
        api.get<{ categories: ICategory[] }>('/config/categories'),
      ]);
      if (tplRes.success) {
        setTemplates(tplRes.data.templates.filter((t) => t.source === 'config_plus'));
      }
      if (rulesRes.success) setScoringRules(rulesRes.data);
      if (catsRes.success) setCategories(catsRes.data.categories.filter((c) => c.engine_mode === 'rule_engine'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Edit fields for config dialog ─────────────────────── */
  const [configDisplayName, setConfigDisplayName] = useState('');
  const [configDescription, setConfigDescription] = useState('');

  /* ── Helpers ─────────────────────────────────────────────── */
  const getActiveCategoryCount = (tpl: IScanTemplate): number => {
    const tcs = (tpl.template_categories ?? []) as IScanTemplateCategory[];
    return tcs.filter((tc) => tc.is_active).length;
  };

  const getScoringRuleCount = (tpl: IScanTemplate): number => {
    return ((tpl as unknown as { template_scoring_rules?: ITemplateScoringRule[] }).template_scoring_rules ?? []).length;
  };

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((tpl) => {
        if (!showInactive && !tpl.is_active) return false;
        if (!q) return true;
        return tpl.display_name.toLowerCase().includes(q) ||
          tpl.internal_name.toLowerCase().includes(q) ||
          (tpl.description ?? '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.is_active === b.is_active) return a.display_name.localeCompare(b.display_name);
        return a.is_active ? -1 : 1;
      });
  }, [templates, search, showInactive]);

  /** Filter scoring rules to only those whose consequences target categories in the given set */
  const filterRulesByCategories = (rules: IScoringRule[], categoryIds: string[]): IScoringRule[] => {
    if (categoryIds.length === 0) return rules.filter((r) => r.active);
    return rules.filter((r) => {
      if (!r.active) return false;
      const consequences = r.consequences ?? [];
      if (consequences.length === 0) return true;
      return consequences.some((c) => categoryIds.includes(c.category_id));
    });
  };

  /* ── Toggle Active ──────────────────────────────────────── */
  const handleToggleActive = async (tpl: IScanTemplate) => {
    try {
      await api.patch(`/config/scan-templates/${tpl.id}`, { is_active: !tpl.is_active });
      fetchData();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* ── Duplicate Template ──────────────────────────────────── */
  const handleDuplicate = async (tpl: IScanTemplate) => {
    try {
      const res = await api.post(`/config/scan-templates/${tpl.id}/duplicate`, {});
      if (res.success) {
        fetchData();
      } else {
        setError((res as { success: false; error: { message: string } }).error.message);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* ── Delete Template ─────────────────────────────────────── */
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.delete(`/config/scan-templates/${deleteTarget.id}`);
      if (res.success) {
        setDeleteTarget(null);
        fetchData();
      } else {
        setError((res as { success: false; error: { message: string } }).error.message);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* ── Full Config Dialog (Categories + Scoring Rules) ─────── */
  const openConfigDialog = async (tpl: IScanTemplate) => {
    setConfigTarget(tpl);
    setConfigDisplayName(tpl.display_name);
    setConfigDescription(tpl.description ?? '');
    setConfigDevOnly(tpl.dev_only ?? false);
    setConfigTab(0);

    // Build ordered list from existing template_categories (sorted by slot)
    const existingCats = (tpl.template_categories ?? []) as IScanTemplateCategory[];
    const activeSorted = existingCats
      .filter((tc) => tc.is_active)
      .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));

    setConfigCategories(activeSorted.map((tc) => ({ category_id: tc.category_id })));

    // Load scoring rules for this template
    try {
      const res = await api.get<{ scoring_rule_id: string }[]>(`/config-plus/templates/${tpl.id}/scoring-rules`);
      if (res.success && Array.isArray(res.data)) {
        setConfigRuleIds(res.data.map((r) => r.scoring_rule_id));
      } else {
        setConfigRuleIds([]);
      }
    } catch (_e) {
      setConfigRuleIds([]);
    }
  };

  const toggleConfigRule = (ruleId: string) => {
    setConfigRuleIds((prev) =>
      prev.includes(ruleId) ? prev.filter((id) => id !== ruleId) : [...prev, ruleId]
    );
  };

  const configActiveCategoryIds = configCategories.map((c) => c.category_id);
  const filteredConfigRules = filterRulesByCategories(scoringRules, configActiveCategoryIds);

  const handleSaveConfig = async () => {
    if (!configTarget) return;
    try {
      // Save display name, description & dev_only
      await api.patch(`/config/scan-templates/${configTarget.id}`, {
        display_name: configDisplayName.trim(),
        description: configDescription.trim() || null,
        dev_only: configDevOnly,
      });

      // Save categories (slot = position index + 1)
      const catPayload = configCategories.map((c, idx) => ({
        category_id: c.category_id,
        slot: idx + 1,
        is_active: true,
      }));

      await api.put(`/config/scan-templates/${configTarget.id}/categories`, { categories: catPayload });

      // Save scoring rules
      await api.put(`/config-plus/templates/${configTarget.id}/scoring-rules`, {
        scoring_rule_ids: configRuleIds,
      });

      setConfigTarget(null);
      fetchData();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* ── Create Wizard Logic ─────────────────────────────────── */
  const openWizard = () => {
    setActiveStep(0);
    setFormInternalName('');
    setFormDisplayName('');
    setFormDescription('');
    setWizardCategories([]);
    setWizardRuleIds([]);
    setWizardOpen(true);
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const toggleWizardRule = (ruleId: string) => {
    setWizardRuleIds((prev) =>
      prev.includes(ruleId) ? prev.filter((id) => id !== ruleId) : [...prev, ruleId]
    );
  };

  const wizardActiveCategoryIds = wizardCategories.map((c) => c.category_id);
  const filteredWizardRules = filterRulesByCategories(scoringRules, wizardActiveCategoryIds);

  const slugify = (s: string) =>
    s.toLowerCase()
      .replace(/[\s\-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');

  const existingInternalNames = templates.map((t) => t.internal_name);
  const generatedInternalName = (() => {
    const base = slugify(formDisplayName);
    if (!base) return '';
    let candidate = base;
    let i = 2;
    while (existingInternalNames.includes(candidate)) candidate = `${base}_${i++}`;
    return candidate;
  })();

  const isStep1Valid = formDisplayName.trim().length >= 1;

  const handleCreateTemplate = async () => {
    setCreating(true);
    try {
      const createRes = await api.post<{ template: IScanTemplate }>('/config/scan-templates', {
        internal_name: generatedInternalName,
        display_name: formDisplayName.trim(),
        description: formDescription.trim() || null,
        source: 'config_plus',
      });
      if (!createRes.success) {
        setError(createRes.error.message);
        setCreating(false);
        return;
      }
      const newId = createRes.data.template.id;

      // Assign categories (slot = position + 1)
      const catPayload = wizardCategories.map((c, idx) => ({
        category_id: c.category_id,
        slot: idx + 1,
        is_active: true,
      }));

      if (catPayload.length > 0) {
        await api.put(`/config/scan-templates/${newId}/categories`, { categories: catPayload });
      }

      // Assign scoring rules
      if (wizardRuleIds.length > 0) {
        await api.put(`/config-plus/templates/${newId}/scoring-rules`, {
          scoring_rule_ids: wizardRuleIds,
        });
      }

      setWizardOpen(false);
      fetchData();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <Typography>Lade Templates...</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Templates</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Toolbar: Suche + Toggle + Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Template suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Inaktive einblenden</Typography>}
          sx={{ ml: 0 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openWizard}
          sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' }, whiteSpace: 'nowrap' }}
        >
          Template erstellen
        </Button>
      </Box>

      <Stack spacing={2}>
        {filteredTemplates.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {search ? 'Keine Treffer für die Suche.' : 'Noch keine Templates vorhanden.'}
          </Typography>
        ) : filteredTemplates.map((tpl) => (
          <Card key={tpl.id} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {tpl.emoji && `${tpl.emoji} `}{tpl.display_name}
                  </Typography>
                  {tpl.description && (
                    <Typography variant="body2" color="text.secondary">{tpl.description}</Typography>
                  )}
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {tpl.is_default && <Chip label="Default" size="small" sx={{ bgcolor: colors.lime, color: colors.black }} />}
                    <Chip label={`${getActiveCategoryCount(tpl)} Kategorien`} size="small" variant="outlined" />
                    <Chip label={`${getScoringRuleCount(tpl)} Scoring-Regeln`} size="small" variant="outlined" />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Chip
                    label={tpl.is_active ? 'Aktiv' : 'Inaktiv'}
                    size="small"
                    color={tpl.is_active ? 'success' : 'default'}
                    variant="outlined"
                  />
                  <Switch
                    size="small"
                    checked={tpl.is_active}
                    onChange={() => handleToggleActive(tpl)}
                    title={tpl.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  />
                  <Tooltip title="Template konfigurieren">
                    <IconButton size="small" onClick={() => openConfigDialog(tpl)}>
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplizieren">
                    <IconButton size="small" onClick={() => handleDuplicate(tpl)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton size="small" onClick={() => setDeleteTarget(tpl)} color="error">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* ── Delete Confirmation Dialog ──────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Template löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du das Template <strong>{deleteTarget?.display_name}</strong> wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full Config Dialog (Categories + Scoring Rules) ── */}
      <Dialog open={!!configTarget} onClose={() => setConfigTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Template konfigurieren
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', overflowY: 'auto', maxHeight: '70vh' }}>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <TextField
              label="Display Name"
              value={configDisplayName}
              onChange={(e) => setConfigDisplayName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Beschreibung"
              value={configDescription}
              onChange={(e) => setConfigDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
          </Stack>
          <Tabs value={configTab} onChange={(_, v) => setConfigTab(v)} sx={{ mb: 2 }}>
            <Tab label="Kategorien" />
            <Tab label="Scoring-Regeln" />
          </Tabs>

          {/* Tab 0: Categories with Drag & Drop */}
          {configTab === 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Wähle bis zu 3 Kategorien aus. Reihenfolge per Drag & Drop ändern — Position bestimmt den Slot (oben = Slot 1).
              </Typography>
              <DragCategoryList
                items={configCategories}
                categories={categories}
                onChange={setConfigCategories}
                maxItems={3}
              />
            </Box>
          )}

          {/* Tab 1: Scoring Rules — grouped by category with Switch */}
          {configTab === 1 && (
            <Box>
              <TextField
                size="small"
                placeholder="Scoring-Regel suchen…"
                value={configRuleSearch}
                onChange={(e) => setConfigRuleSearch(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {configActiveCategoryIds.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                  Keine Kategorien aktiv — bitte zuerst Kategorien zuweisen.
                </Typography>
              ) : (
                configActiveCategoryIds.map((catId) => {
                  const cat = categories.find((c) => c.id === catId);
                  const q = configRuleSearch.trim().toLowerCase();
                  const catRules = scoringRules.filter((r) =>
                    r.active &&
                    r.consequences?.some((c) => c.category_id === catId) &&
                    (!q || r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q))
                  );
                  const activeCount = catRules.filter((r) => configRuleIds.includes(r.id)).length;
                  return (
                    <Accordion key={catId} defaultExpanded={false} disableGutters
                      sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 44,
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {cat?.display_name ?? catId}
                          </Typography>
                          {activeCount > 0 && (
                            <Chip
                              label={activeCount}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: 11 }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        {catRules.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            {q ? 'Keine Treffer.' : 'Keine Regeln für diese Kategorie.'}
                          </Typography>
                        ) : (
                          catRules.map((rule) => (
                            <Box
                              key={rule.id}
                              sx={{
                                display: 'flex', alignItems: 'center', py: 0.75,
                                borderBottom: '1px solid', borderColor: 'divider',
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                  {rule.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {`Impact ${rule.impact_level}`}
                                  {rule.is_showstopper && ' · Showstopper'}
                                  {rule.consequences && rule.consequences.length > 1 && (
                                    <> · wirkt auf: {rule.consequences.map((c) => c.category?.display_name ?? c.category_id).join(', ')}</>
                                  )}
                                </Typography>
                              </Box>
                              <Switch
                                size="small"
                                checked={configRuleIds.includes(rule.id)}
                                onChange={() => toggleConfigRule(rule.id)}
                              />
                            </Box>
                          ))
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ flex: 1 }}>
            {devMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={configDevOnly}
                    onChange={(e) => setConfigDevOnly(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Entwickler only</Typography>}
              />
            )}
          </Box>
          <Button onClick={() => setConfigTarget(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={!configDisplayName.trim()}
            sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' } }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Template Wizard ──────────────────────────── */}
      <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neues Template erstellen</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {WIZARD_STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {/* Step 1: Basic Info */}
          {activeStep === 0 && (
            <Stack spacing={3}>
              <TextField
                label="Template Name"
                value={formDisplayName}
                onChange={(e) => setFormDisplayName(e.target.value)}
                autoFocus
                fullWidth
                helperText="Name des Templates (z.B. Domain Screener)"
              />
              <TextField
                label="Beschreibung"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </Stack>
          )}

          {/* Step 2: Categories with Drag & Drop */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Kategorien zuweisen (max. 3)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Füge Kategorien hinzu und ordne sie per Drag & Drop. Die Position bestimmt den Slot (oben = Slot 1, Mitte = Slot 2, unten = Slot 3).
              </Typography>
              <DragCategoryList
                items={wizardCategories}
                categories={categories}
                onChange={setWizardCategories}
                maxItems={3}
              />
            </Box>
          )}

          {/* Step 3: Scoring Rules (filtered by selected categories) */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Scoring-Regeln zuordnen
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Nur Regeln, die auf die gewählten Kategorien wirken, werden angezeigt. Dieser Schritt ist optional.
              </Typography>
              <List dense>
                {filteredWizardRules.map((rule) => (
                  <ListItem key={rule.id} dense onClick={() => toggleWizardRule(rule.id)} sx={{ cursor: 'pointer', borderRadius: 2 }}>
                    <ListItemIcon>
                      <Checkbox edge="start" checked={wizardRuleIds.includes(rule.id)} />
                    </ListItemIcon>
                    <ListItemText
                      primary={rule.name}
                      secondary={
                        <>
                          {`Impact ${rule.impact_level}${rule.is_showstopper ? ' · Showstopper' : ''}`}
                          {rule.consequences && rule.consequences.length > 0 && (
                            <>{` · Kategorien: ${rule.consequences.map((c) => c.category?.display_name ?? c.category_id).join(', ')}`}</>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {filteredWizardRules.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                  {wizardActiveCategoryIds.length === 0
                    ? 'Keine Kategorien ausgewählt — gehe zurück und füge Kategorien hinzu.'
                    : 'Keine passenden Scoring-Regeln für die gewählten Kategorien gefunden.'}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWizardOpen(false)}>Abbrechen</Button>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <Button onClick={handleBack}>Zurück</Button>
          )}
          {activeStep < WIZARD_STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && !isStep1Valid}
              sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' } }}
            >
              Weiter
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateTemplate}
              disabled={creating}
              sx={{ bgcolor: colors.lime, color: colors.black, '&:hover': { bgcolor: '#b8d900' } }}
            >
              {creating ? 'Erstelle...' : 'Template erstellen'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
